import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Auto-applies discount codes from copy-to-clipboard functionality
 */

/**
 * Stores a discount code for later application when cart becomes available
 * @param {string} discountCode - The discount code to store
 */
function storePendingDiscountCode(discountCode) {
  try {
    sessionStorage.setItem('pendingDiscountCode', discountCode);
  } catch (error) {
  }
}

/**
 * Clears any pending discount code from session storage
 */
function clearPendingDiscountCode() {
  try {
    sessionStorage.removeItem('pendingDiscountCode');
  } catch (error) {
  }
}

/**
 * Gets any pending discount code from session storage
 * @returns {string|null} - The pending discount code or null if none exists
 */
function getPendingDiscountCode() {
  try {
    const discountCode = sessionStorage.getItem('pendingDiscountCode');
    return discountCode;
  } catch (error) {
    return null;
  }
}

/**
 * Submits a form and waits for the discount update event
 * @param {HTMLFormElement} form - The form to submit
 * @returns {Promise<void>} - Promise that resolves when discount update is complete
 */
function submitFormAsync(form) {
  return new Promise((resolve, reject) => {
    /** @type {(() => void) | undefined} */
    let handleDiscountUpdate;
    /** @type {number | undefined} */
    let timeoutId;
    
    try {

      handleDiscountUpdate = () => {
        document.removeEventListener(ThemeEvents.discountUpdate, /** @type {EventListener} */ (handleDiscountUpdate));
        clearTimeout(timeoutId);
        
        resolve();
      };

      timeoutId = setTimeout(() => {
        document.removeEventListener(ThemeEvents.discountUpdate, /** @type {EventListener} */ (handleDiscountUpdate));
        
        resolve();
      }, 3000); // Fallback timeout

      document.addEventListener(ThemeEvents.discountUpdate, /** @type {EventListener} */ (handleDiscountUpdate));

      form.requestSubmit();
    } catch (error) {
      if (handleDiscountUpdate) {
        document.removeEventListener(ThemeEvents.discountUpdate, /** @type {EventListener} */ (handleDiscountUpdate));
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      reject(error);
    }
  });
}

/**
 * Shared function to automatically apply a discount code to the cart
 * @param {string} discountCode - The discount code to apply
 * @returns {Promise<boolean>} - Returns true if discount was applied successfully
 */
export async function autoApplyDiscountCode(discountCode) {
  try {
    if (!discountCode || typeof discountCode !== 'string') {
      return false;
    }

    const trimmedCode = discountCode.trim();
    if (!trimmedCode) {
      return false;
    }

    // Find the cart discount input in the cart drawer specifically
    const cartDrawer = document.querySelector('cart-drawer-component');
    const cartDiscountInput = cartDrawer ? cartDrawer.querySelector('#cart-discount') : document.querySelector('#cart-discount');
    
    if (!cartDiscountInput || !(cartDiscountInput instanceof HTMLInputElement)) {
      storePendingDiscountCode(trimmedCode);
      return false;
    }

    // Find the cart discount form
    const cartDiscountForm = cartDiscountInput.closest('form');
    if (!cartDiscountForm) {
      storePendingDiscountCode(trimmedCode);
      return false;
    }

    // Set the discount code value and submit
    cartDiscountInput.value = trimmedCode;

    await submitFormAsync(cartDiscountForm);
    
    // Show discount popup and mark as shown for this session
    if (typeof window !== "undefined" && /** @type {any} */ (window).showDiscountCodeNotification) {
      /** @type {any} */ (window).showDiscountCodeNotification(trimmedCode);
      sessionStorage.setItem('discountPopupShown', 'true');
    }
    
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Checks for and applies any pending discount codes when cart becomes available
 */
export async function checkAndApplyPendingDiscountCode() {
  try {
    const pendingDiscountCode = getPendingDiscountCode();
    
    if (pendingDiscountCode) {
      // Wait for cart content to be fully loaded
      await new Promise(resolve => setTimeout(resolve, 100));
      
      clearPendingDiscountCode();
      const success = await autoApplyDiscountCode(pendingDiscountCode);
    } else {
      // no pending discount codes
    }
  } catch (error) {
  }
}

// Listen for cart events to check for pending discount codes
document.addEventListener(CartAddEvent.eventName, async () => {
  try {
    await checkAndApplyPendingDiscountCode();
  } catch (error) {
  }
});

document.addEventListener(ThemeEvents.cartUpdate, async () => {
  try {
    await checkAndApplyPendingDiscountCode();
  } catch (error) {
  }
});
